import { Toaster } from 'react-hot-toast'
import { Routes, Route, NavLink, Navigate, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import DataExtraction from './components/DataExtraction'
import TemplateManager from './components/TemplateManager'
import CrawlerManagement from './components/CrawlerManagement'
import DataHistory from './components/DataHistory'
import JobManager from './components/JobManager'
import GlobalCrawlStatus from './components/GlobalCrawlStatus'
import ProfilePage from './components/ProfilePage'
import SettingsPage from './components/SettingsPage'
import Login from './components/Login'
import ProtectedRoute from './components/ProtectedRoute'
import Sidebar from './components/Sidebar'
import { useAuth } from './context/AuthContext'

function App() {
  const location = useLocation()
  const { t } = useTranslation()
  
  // Public routes (no authentication required)
  if (location.pathname === '/login') {
    return (
      <>
        <Toaster position="top-right" />
        <Routes>
          <Route path="/login" element={<Login />} />
        </Routes>
      </>
    )
  }
  
  // Protected routes with special layouts (profile, settings)
  if (location.pathname === '/profile' || location.pathname === '/settings') {
    return (
      <>
        <Toaster position="top-right" />
        <Routes>
          <Route path="/profile" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
          <Route path="/settings" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
        </Routes>
      </>
    )
  }

  const tabs = [
    { id: 'data', label: t('nav.dataExtractor'), icon: 'fa-spider', path: '/' },
    { id: 'templates', label: t('nav.templates'), icon: 'fa-layer-group', path: '/templates' },
    { id: 'jobs', label: t('nav.scheduledJobs'), icon: 'fa-briefcase', path: '/jobs' },
    { id: 'history', label: t('nav.history'), icon: 'fa-clock-rotate-left', path: '/history' },
    { id: 'management', label: t('nav.crawlerMonitoring'), icon: 'fa-gears', path: '/monitor' }
  ]

  return (
    <ProtectedRoute>
      <div className="flex h-screen overflow-hidden bg-light-100">
        <Toaster position="top-right" />
      
      {/* Sidebar Component with Language Switcher */}
      <Sidebar />

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Header */}
        <header className="bg-white border-b border-light-200/20 shadow-sm">
          <div className="px-8 py-4 flex items-center justify-between">
            <div className="flex items-center space-x-6">
              <div>
                <h1 className="text-2xl font-bold text-dark-300 flex items-center">
                  <i className="fa-solid fa-spider text-primary-500 mr-3"></i>
                  {t('header.title')}
                </h1>
                <p className="text-sm text-dark-100 mt-0.5">{t('header.subtitle')}</p>
              </div>
            </div>
            
            <div className="flex items-center space-x-4">
              <div className="relative">
                <input 
                  type="text" 
                  placeholder={t('header.searchPlaceholder')}
                  className="w-80 pl-10 pr-4 py-2 border border-light-200/30 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
                />
                <i className="fa-solid fa-search absolute left-3 top-3 text-light-200"></i>
              </div>
              
              
            </div>
          </div>
          
          {/* Tabs */}
          <div className="px-8 flex space-x-6 border-t border-light-200/10">
            {tabs.map((tab) => (
              <NavLink
                key={tab.id}
                to={tab.path}
                className={({ isActive }) => `px-1 py-3 text-sm font-medium border-b-2 transition-colors ${
                  isActive
                    ? 'text-primary-500 border-primary-500'
                    : 'text-dark-100 hover:text-dark-300 border-transparent hover:border-light-200/30'
                }`}
              >
                {tab.label}
              </NavLink>
            ))}
          </div>
        </header>

        {/* Content Area */}
        <div className="flex-1 overflow-auto">
          {/* Global Status */}
          <div className="max-w-full px-8 pt-6">
            <GlobalCrawlStatus />
          </div>

          {/* Tab Content */}
          <div className="px-8 py-6">
            <Routes>
              <Route path="/" element={<DataExtraction />} />
              <Route path="/templates" element={<TemplateManager />} />
              <Route path="/jobs" element={<JobManager />} />
              <Route path="/history" element={<DataHistory />} />
              <Route path="/monitor" element={<CrawlerManagement />} />
            </Routes>
          </div>
        </div>
      </div>
    </div>
    </ProtectedRoute>
  )
}

export default App
